<template>
    <main class="details">
        <section>
            <div class="container details__hero-container">
                <header class="details__header">
                    <h1>{{ activeTitle }}</h1>
                    <p class="intro">
                        Samfunnsøkonomisk analyse AS har beregnet de årlige kostnadene ved å kjøpe og bruke ny bil for noen utvalgte biltyper.
                    </p>
                    <p>
                        Beregningene er gjort for en {{ activeCarType }} til en kjøpspris på {{ activeCarPrice }}
                    </p>
                    <p class="details__text-list-heading">
                        Andre viktige forutsetninger vi har lagt til grunn er:
                    </p>
                    <ul class="details__text-list">
                        <li>Vi har lagt til grunn kjøp av ny bil</li>
                        <li>Gjennomsnittlig eierforhold på 10 år</li>
                        <li>Gjennomsnittlig kjørelengde per år på 12.000 km (samlet 120.000 km over 10 år)</li>
                    </ul>
                    <a
                        class="link link--icon-right details__download-link"
                        href="/docs/2024_Rapport_Kostnader_eie_bil.pdf"
                        target="_blank">
                        Last ned hele rapporten <icon name="arrow-right" />
                    </a>
                </header>
                <div class="details__specs">
                    <div class="details__specs-list-wrap">
                        <ul class="details__specs-list">
                            <li
                                v-for="(d, i) in detailsList"
                                :key="i"
                                class="details__specs-item">
                                <span>{{ d.label }}</span>
                                <span>{{ formatAmount(d.value) }}</span>
                            </li>
                        </ul>
                        <div class="details__specs-total">
                            <span>Sum</span> <span>{{ calcTotal }}</span>
                        </div>
                    </div>
                    <div class="details__specs-note">
                        <p class="micro">
                            Oppsummering av beregningene. 2024 – kroner per år.
                        </p>
                    </div>
                </div>
            </div>
        </section>
        <section>
            <div class="container details__hero-container">
                <header class="details__header">
                    <h1>Eksempler på hva man kunne brukt penger på istedenfor kostnader ved å eie bil</h1>
                    <p class="intro">
                        Samfunnsøkonomisk analyse AS har også beregnet hva du kunne kjøpt for prisen av å eie en bil
                    </p>
                </header>
                <div class="details__specs">
                    <div class="details__specs-list-wrap">
                        <ul class="details__specs-list">
                            <li
                                v-for="(activeComparision, i) in activeComparisons"
                                :key="i"
                                class="details__specs-item">
                                <span>{{ activeComparision.units.replace('#value#', activeComparision.value) }} {{ activeComparision.textDetails || activeComparision.text }}</span>
                                <span>{{ formatAmount(activeComparision.annualPrice) }}</span>
                            </li>
                        </ul>
                        <div class="details__specs-total">
                            <span>Rest</span> <span>{{ formatAmount(activeRest) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section>
            <div class="container details__show-more">
                <router-link to="/" class="btn">
                    Tilbake til kalkulatoren
                </router-link>
            </div>
        </section>
        <section class="details__share-section-bottom">
            <div class="container details__share-container-bottom">
                <share class="details__share details__share--bottom" />
            </div>
        </section>
    </main>
</template>

<script>
import { formatNOK } from '@/utils/currency';
import { kebabCase } from 'lodash';
import Icon from '@/components/Icon';
import Share from '@/components/Share';
export default {
    name: 'Details',
    components: {
        Icon,
        Share
    },
    computed: {
        activeComparisons() {
            return (this.$store.getters.activeComparisons) || '';
        },
        activeRest () {
            let comparePrice = (this.$store.getters.activeSelection && this.$store.getters.activeSelection.total) || 0;
            return comparePrice - ((this.activeComparisons || []).map((v)=>{
                return v.value * v.priceInfo.price * v.priceInfo.unitsPerYear;
            })).reduce((t, v) => (t + v));
        },
        activeTitle () {
            return (this.$store.getters.activeSelection && this.$store.getters.activeSelection.title) || '';
        },
        activeCarType () {
            return (this.$store.getters.activeSelection && this.$store.getters.activeSelection.type) || '';
        },
        activeCarPrice () {
            return (this.$store.getters.activeSelection && this.$store.getters.activeSelection.price && formatNOK(this.$store.getters.activeSelection.price, ' kr')) || '';
        },
        detailsList () {
            return (Array.isArray(this.$store.getters.activeDetails) && this.$store.getters.activeDetails) || '';
        },
        calcTotal () {
            return formatNOK(this.detailsList.map(item => item.value).reduce((t, v) => (t + v)));
        }
    },
    methods: {
        formatAmount (amount) {
            return formatNOK(amount.toFixed(0));
        }
    }
};
</script>

<style lang="scss">
.details{
    position: relative;
    &__hero-container{
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
    }
    &__header{
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        @include breakpoint(large){
            width: calc(50% - #{spacing(6)});
        }
    }
    &__text-list-heading{
        margin-bottom: 0;
    }
    &__text-list{
        margin: 0;
        padding: 0 1.5em;
    }
    &__download-link{
        margin-top: 2em;
        @include breakpoint(large){
            margin-top: auto;
        }
        margin-bottom: spacing(3);
    }
    &__specs{
        position: relative;
        width: 100%;
        @include breakpoint(large){
            width: calc(50% - #{spacing(6)});
        }
    }
    &__specs-list-wrap{
        background-color: color(light-grey);
    }
    &__specs-list{
        list-style: none;
        margin: 0;
        padding: spacing(6);
        @include breakpoint(small only){
            padding: spacing(3);
        }
    }
    &__specs-item{
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        margin-bottom: spacing(3);
        @include breakpoint(small only){
            margin-bottom: spacing(2);
        }
        &:last-child{
            margin-bottom: 0;
        }
    }
    &__specs-total{
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        text-align: right;
        padding: spacing(3) spacing(6);
        @include breakpoint(small only){
            padding: spacing(2) spacing(3);
        }
        color: color(white);
        background-color: color(dark-grey);
    }
    &__specs-note{
        position: absolute;
        width: 100%;
        top: auto;
        left: 0;
        text-align: right;
    }
    &__stamp{
        width: 100%;
        padding: 0;
        &-container{
            text-align: center;
        }
    }
    &__show-more{
        display: flex;
        justify-content: center;
    }
    &__share-container-bottom{
        display: flex;
        justify-content: center;
    }
}
</style>
